import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';

import { CriticalTableDialogService } from '../../tables/critical-values-table/critical-table-dialog.service';
import type { TestResult } from '../../tests/test-runner/test-runner.model';
import { buildSampleStatFormula, buildTestMeasureFormula, fmtNum, fmtP } from './test-measures-formula.util';
import {
  getSampleStatDefs,
  getTestMeasureDefs,
  SAMPLE_STAT_COLOR,
  type SampleStatDef,
  type TestMeasureDef,
  TEST_MEASURE_COLORS,
  type TestMeasureId,
} from './test-measures.model';

// ---------------------------------------------------------------------------
// View-model interfaces
// ---------------------------------------------------------------------------

interface TestMeasureRow {
  readonly def: TestMeasureDef;
  readonly formattedValue: string;
  readonly formula: string;
}

interface SampleStatRow {
  readonly def: SampleStatDef;
  readonly value: number;
  readonly formattedValue: string;
  readonly formula: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-test-measures-panel',
  standalone: true,
  imports: [MatExpansionModule, MatTooltipModule, IconComponent, MarkdownViewerComponent],
  providers: [CriticalTableDialogService],
  templateUrl: './test-measures-panel.component.html',
  styleUrl: './test-measures-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestMeasuresPanelComponent {
  constructor(private readonly criticalTableDialog: CriticalTableDialogService) {}

  readonly result = input.required<TestResult>();
  readonly highlightedMeasure = input<TestMeasureId | null>(null);

  readonly highlightedMeasureChange = output<TestMeasureId | null>();

  // ── Colors ──────────────────────────────────────────────────────────────

  readonly measureColors = TEST_MEASURE_COLORS;
  readonly sampleStatColor = SAMPLE_STAT_COLOR;

  // ── Decision ────────────────────────────────────────────────────────────

  readonly rejectNull = computed(() => this.result().rejectNull);

  readonly decisionLabel = computed(() => {
    return this.result().rejectNull ? `Reject $H_0$` : `Fail to reject $H_0$`;
  });

  readonly decisionSubtitle = computed(() => {
    const pct = Math.round(this.result().alpha * 100);
    return `at the ${pct}% significance level`;
  });

  readonly evidenceStrength = computed(() => {
    const r = this.result();
    return evidenceStrength(r.pValue, r.rejectNull);
  });

  // ── Test results rows ──────────────────────────────────────────────────

  readonly testMeasures = computed<TestMeasureRow[]>(() => {
    const r = this.result();
    const defs = getTestMeasureDefs(r);

    return defs.map((def) => ({
      def,
      formattedValue: formatTestMeasureValue(def.id, r),
      formula: buildTestMeasureFormula(def.id, r),
    }));
  });

  // ── Sample statistics rows ─────────────────────────────────────────────

  readonly sampleStats = computed<SampleStatRow[]>(() => {
    const r = this.result();
    const defs = getSampleStatDefs(r);

    return defs.map((def) => {
      const value = r.sampleStats[def.key];
      return {
        def,
        value,
        formattedValue: fmtNum(value),
        formula: buildSampleStatFormula(def.key, value),
      };
    });
  });

  // ── Interpretation ─────────────────────────────────────────────────────

  readonly interpretationLines = computed<string[]>(() => {
    const r = this.result();
    return [buildStatisticLocationLine(r), buildPValueComparisonLine(r), buildConclusionLine(r)];
  });

  // ── Expand/collapse state ──────────────────────────────────────────────

  private readonly expandedSections = signal<Set<string>>(new Set());

  isExpanded(key: string): boolean {
    return this.expandedSections().has(key);
  }

  onPanelOpened(key: string): void {
    this.expandedSections.update((set) => {
      const next = new Set(set);
      next.add(key);
      return next;
    });
    if (isTestMeasureId(key)) {
      this.highlightedMeasureChange.emit(key);
    }
  }

  onPanelClosed(key: string): void {
    this.expandedSections.update((set) => {
      const next = new Set(set);
      next.delete(key);
      return next;
    });
    if (isTestMeasureId(key) && this.highlightedMeasure() === key) {
      this.highlightedMeasureChange.emit(null);
    }
  }

  // ── Critical table dialog ───────────────────────────────────────────────

  openCriticalTable(): void {
    this.criticalTableDialog.open({ result: this.result() });
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatTestMeasureValue(id: TestMeasureId, r: TestResult): string {
  switch (id) {
    case 'testStatistic':
      return fmtNum(r.testStatistic);
    case 'degreesOfFreedom':
      return fmtNum(r.degreesOfFreedom);
    case 'criticalValue': {
      if (r.criticalValues.length === 2) {
        return `\u00B1${fmtNum(r.criticalValues[1])}`;
      }
      return fmtNum(r.criticalValues[0]);
    }
    case 'pValue':
      return r.pValue < 0.0001 ? '< 0.0001' : fmtP(r.pValue);
  }
}

// ---------------------------------------------------------------------------
// Interpretation helpers (migrated from test-runner.component.ts)
// ---------------------------------------------------------------------------

function buildStatisticLocationLine(r: TestResult): string {
  const stat = fmtNum(r.testStatistic);
  const region = r.rejectNull ? 'rejection' : 'acceptance';

  if (r.distributionType === 't') {
    const cvLabel =
      r.criticalValues.length === 1 ? `t^* = ${fmtNum(r.criticalValues[0])}` : `t^* = ${fmtNum(r.criticalValues[1])}`;

    if (r.tailType === 'two') {
      const op = r.rejectNull ? '>' : '<';
      return `The test statistic $t = ${stat}$ falls in the ${region} region ($|t| ${op} ${cvLabel}$).`;
    }
    const op = r.tailType === 'right' ? (r.rejectNull ? '>' : '<') : r.rejectNull ? '<' : '>';
    return `The test statistic $t = ${stat}$ falls in the ${region} region ($t ${op} ${cvLabel}$).`;
  }

  if (r.distributionType === 'chi-squared') {
    const cv = fmtNum(r.criticalValues[0]);
    const op = r.rejectNull ? '>' : '<';
    return `The test statistic $\\chi^2 = ${stat}$ falls in the ${region} region ($\\chi^2 ${op} \\chi^2_{\\text{crit}} = ${cv}$).`;
  }

  return `The test statistic $S^+ = ${stat}$ falls in the ${region} region.`;
}

function buildPValueComparisonLine(r: TestResult): string {
  const alphaStr = `\\alpha = ${r.alpha}`;
  const pDisplay = r.pValue < 0.0001 ? `p < 0.0001` : `p = ${fmtP(r.pValue)}`;
  const decision = r.rejectNull ? '**reject**' : '**fail to reject**';
  const op = r.rejectNull ? '<' : '>';

  return `Since $${pDisplay} ${op} ${alphaStr}$, we ${decision} $H_0$.`;
}

function buildConclusionLine(r: TestResult): string {
  const strength = evidenceStrength(r.pValue, r.rejectNull);

  const conclusions: Record<string, [string, string]> = {
    'one-sample-t': [
      `that the population mean differs from ${
        r.nullMean !== null ? `$\\mu_0 = ${r.nullMean}$` : 'the hypothesised mean'
      }`,
      `that the population mean differs from ${
        r.nullMean !== null ? `$\\mu_0 = ${r.nullMean}$` : 'the hypothesised mean'
      }`,
    ],
    'two-sample-t': ['that the two population means differ', 'that the two population means differ'],
    'paired-t': [
      'of a difference between the paired measurements',
      'there is a difference between the paired measurements',
    ],
    'chi-squared-independence': [
      'of an association between the two variables',
      'there is an association between the two variables',
    ],
    'chi-squared-gof': [
      'that the observed frequencies differ from the expected distribution',
      'the observed frequencies differ from the expected distribution',
    ],
    'sign-test': ['that the median difference is not zero', 'the median difference is not zero'],
  };

  const [rejectText, failText] = conclusions[r.testType] ?? [
    'against the null hypothesis',
    'the null hypothesis is false',
  ];

  return r.rejectNull
    ? `There is ${strength} evidence ${rejectText}.`
    : `There is ${strength} evidence to conclude ${failText}.`;
}

function evidenceStrength(p: number, reject: boolean): string {
  if (!reject) {
    return 'insufficient';
  }
  if (p < 0.001) {
    return 'very strong';
  }
  if (p < 0.01) {
    return 'strong';
  }
  return 'moderate';
}

const TEST_MEASURE_IDS = new Set<string>(['testStatistic', 'degreesOfFreedom', 'criticalValue', 'pValue']);

function isTestMeasureId(key: string): key is TestMeasureId {
  return TEST_MEASURE_IDS.has(key);
}
